import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { photoUrlFromKey } from '@leanin/shared/media-url';
import { useMember } from '@/lib/use-session';
import { SpaceDetailView } from '@/components/spaces/SpaceDetailView';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { spacesKeys } from '@/lib/queries/spaces';
import { groupKeys } from '@/lib/queries/groups';
import { useFeed } from '@/lib/queries/feed';

export const Route = createFileRoute('/_authed/groups/$id/')({
  component: SpaceDetailPage,
});

// The route owns every query the detail page needs and hands each slice to
// `SpaceDetailView` as `T | null` (or `undefined` while pending). The view
// renders its skeleton chrome + per-region fallbacks until each slice
// resolves — there is no parallel skeleton tree.
function SpaceDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();

  const spaceQuery = useQuery({
    ...spacesKeys.detail(id),
    retry: false,
  });

  const membersQuery = useQuery({
    ...groupKeys.members(id, { pageSize: 50 }),
    // Roster is members-only server-side (403 otherwise): fetch only when
    // the viewer actually joined or is a leader/admin.
    enabled:
      spaceQuery.data != null &&
      (spaceQuery.data.viewerHasJoined || spaceQuery.data.viewerRole === 'leader'),
  });
  const relatedQuery = useQuery({
    ...spacesKeys.related(id),
    enabled: spaceQuery.data != null,
  });
  const postsQuery = useFeed(
    { scope: `group:${id}`, pageSize: 24 },
    { enabled: spaceQuery.data != null },
  );

  if (me === null) return null;
  // 404 → branded NotFoundPage directly (render-context `throw notFound()`
  // lands in TanStack's catch-boundary, not its 404 surface).
  if (
    isNotFoundApiError(spaceQuery.error) ||
    (spaceQuery.data === null && !spaceQuery.isLoading)
  ) {
    return <NotFoundPage />;
  }
  if (spaceQuery.isError) throw notFound();

  const space = spaceQuery.data ?? null;
  const members = membersQuery.data?.items ?? null;
  const related = relatedQuery.data ?? null;
  const posts = postsQuery.data?.items ?? null;

  const viewer = {
    id: me.id,
    firstName: me.firstName,
    lastName: me.lastName,
    photoUrl: photoUrlFromKey(me.photoStorageKey, me.photoStatus) ?? undefined,
  };

  return (
    <SpaceDetailView
      space={space}
      viewer={viewer}
      members={members}
      related={related}
      posts={posts}
    />
  );
}
